/* ==========================================================================
   Vali — Minimal Luxury theme
   theme.js — vanilla JS only, no jQuery, no external libraries.
   Built as small Web Components (customElements.define) so each piece of
   UI owns its own markup/behaviour and degrades to plain HTML if JS fails
   (all forms post normally, all links work, without this file).
   ========================================================================== */

(function () {
  "use strict";

  var routes = (window.themeRoutes = window.themeRoutes || {
    cartAdd: "/cart/add.js",
    cartChange: "/cart/change.js",
    cartUpdate: "/cart/update.js",
    cartGet: "/cart.js",
  });

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function trapFocus(container, initialFocus) {
    var focusable = qsa(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter(function (el) {
      return !el.disabled && el.offsetParent !== null;
    });
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    (initialFocus || first).focus();

    function handleKeydown(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener("keydown", handleKeydown);
    return function () {
      container.removeEventListener("keydown", handleKeydown);
    };
  }

  /* ---------------------------------------------------------------------
     Reveal-on-scroll animations
  --------------------------------------------------------------------- */
  function initRevealAnimations() {
    var targets = qsa(".reveal, .reveal-stagger");
    if (!targets.length) return;
    if (!("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Lazy image fade-in (works alongside native loading="lazy")
  --------------------------------------------------------------------- */
  function initLazyImages() {
    qsa("img[loading='lazy']").forEach(function (img) {
      if (img.complete) {
        img.classList.add("is-loaded");
        return;
      }
      img.addEventListener("load", function () {
        img.classList.add("is-loaded");
      });
    });
  }

  /* ---------------------------------------------------------------------
     Sticky header shadow state
  --------------------------------------------------------------------- */
  function initStickyHeader() {
    var header = qs(".site-header");
    if (!header) return;
    window.addEventListener(
      "scroll",
      function () {
        header.classList.toggle("has-scrolled", window.scrollY > 8);
      },
      { passive: true }
    );
  }

  /* ---------------------------------------------------------------------
     Mega menu — click-to-toggle for touch devices (desktop uses CSS
     :hover / :focus-within so this only adds a second, touch-friendly path)
  --------------------------------------------------------------------- */
  function initMegaMenu() {
    qsa(".site-header__nav-item").forEach(function (item) {
      var link = qs(".site-header__nav-link", item);
      var menu = qs(".mega-menu", item);
      if (!link || !menu) return;
      link.addEventListener("click", function (e) {
        if (window.matchMedia("(hover: hover)").matches) return;
        if (!item.classList.contains("is-open")) {
          e.preventDefault();
          qsa(".site-header__nav-item.is-open").forEach(function (open) {
            open.classList.remove("is-open");
          });
          item.classList.add("is-open");
        }
      });
    });
    document.addEventListener("click", function (e) {
      if (!e.target.closest(".site-header__nav-item")) {
        qsa(".site-header__nav-item.is-open").forEach(function (open) {
          open.classList.remove("is-open");
        });
      }
    });
  }

  /* ---------------------------------------------------------------------
     <theme-accordion> — FAQ / collapsible content
  --------------------------------------------------------------------- */
  class ThemeAccordionItem extends HTMLElement {
    connectedCallback() {
      this.trigger = qs(".accordion-item__trigger", this);
      this.panel = qs(".accordion-item__panel", this);
      if (!this.trigger || !this.panel) return;
      this.trigger.addEventListener("click", this.toggle.bind(this));
    }
    toggle() {
      var isOpen = this.getAttribute("data-open") === "true";
      this.setAttribute("data-open", String(!isOpen));
      this.trigger.setAttribute("aria-expanded", String(!isOpen));
      if (!isOpen) {
        this.panel.style.maxHeight = this.panel.scrollHeight + "px";
      } else {
        this.panel.style.maxHeight = "0px";
      }
    }
  }
  customElements.define("theme-accordion-item", ThemeAccordionItem);

  /* ---------------------------------------------------------------------
     <theme-slider> — scroll-snap based slider (testimonials / gallery)
  --------------------------------------------------------------------- */
  class ThemeSlider extends HTMLElement {
    connectedCallback() {
      this.track = qs(".slider__track", this);
      this.prevBtn = qs(".slider__nav-btn--prev", this);
      this.nextBtn = qs(".slider__nav-btn--next", this);
      if (!this.track) return;
      if (this.prevBtn) this.prevBtn.addEventListener("click", this.scrollBy.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener("click", this.scrollBy.bind(this, 1));
    }
    scrollBy(direction) {
      var slide = qs(".slider__slide", this.track);
      var amount = slide ? slide.getBoundingClientRect().width + 24 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: amount * direction, behavior: "smooth" });
    }
  }
  customElements.define("theme-slider", ThemeSlider);

  /* ---------------------------------------------------------------------
     <theme-announcement-bar> — auto-rotating slides
  --------------------------------------------------------------------- */
  class ThemeAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa(".announcement-bar__slide", this);
      if (this.slides.length < 2) return;
      this.index = 0;
      var prev = qs(".announcement-bar__nav--prev", this);
      var next = qs(".announcement-bar__nav--next", this);
      if (prev) prev.addEventListener("click", this.go.bind(this, -1));
      if (next) next.addEventListener("click", this.go.bind(this, 1));
      this.autoplay = setInterval(this.go.bind(this, 1), 5000);
      this.addEventListener("mouseenter", function () {
        clearInterval(this.autoplay);
      }.bind(this));
      this.addEventListener("mouseleave", function () {
        this.autoplay = setInterval(this.go.bind(this, 1), 5000);
      }.bind(this));
    }
    go(direction) {
      this.slides[this.index].classList.remove("is-active");
      this.index = (this.index + direction + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add("is-active");
    }
    disconnectedCallback() {
      clearInterval(this.autoplay);
    }
  }
  customElements.define("theme-announcement-bar", ThemeAnnouncementBar);

  /* ---------------------------------------------------------------------
     <theme-mobile-nav> — off-canvas mobile menu
  --------------------------------------------------------------------- */
  class ThemeMobileNav extends HTMLElement {
    connectedCallback() {
      this.panel = qs(".mobile-nav__panel", this);
      this.releaseFocus = null;
      qsa("[data-mobile-nav-open]").forEach(
        function (btn) {
          btn.addEventListener("click", this.open.bind(this));
        }.bind(this)
      );
      qsa("[data-mobile-nav-close]", this).forEach(
        function (btn) {
          btn.addEventListener("click", this.close.bind(this));
        }.bind(this)
      );
      qs(".mobile-nav__overlay", this).addEventListener("click", this.close.bind(this));
      qsa(".mobile-nav__link[data-has-children]", this).forEach(
        function (link) {
          link.addEventListener("click", function (e) {
            e.preventDefault();
            var submenu = document.getElementById(link.getAttribute("aria-controls"));
            var isOpen = submenu.classList.toggle("is-open");
            link.setAttribute("aria-expanded", String(isOpen));
          });
        }
      );
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && this.classList.contains("is-open")) this.close();
      }.bind(this));
    }
    open() {
      this.classList.add("is-open");
      document.body.style.overflow = "hidden";
      this.releaseFocus = trapFocus(this.panel);
    }
    close() {
      this.classList.remove("is-open");
      document.body.style.overflow = "";
      if (this.releaseFocus) this.releaseFocus();
    }
  }
  customElements.define("theme-mobile-nav", ThemeMobileNav);

  /* ---------------------------------------------------------------------
     <theme-video-section> — click-to-play, lazy-loads the embed
  --------------------------------------------------------------------- */
  class ThemeVideoSection extends HTMLElement {
    connectedCallback() {
      var playBtn = qs(".video-section__play", this);
      if (!playBtn) return;
      playBtn.addEventListener("click", this.play.bind(this));
    }
    play() {
      var template = qs("template", this);
      if (!template) return;
      var frag = template.content.cloneNode(true);
      this.innerHTML = "";
      this.appendChild(frag);
    }
  }
  customElements.define("theme-video-section", ThemeVideoSection);

  /* ---------------------------------------------------------------------
     <theme-product-gallery> — thumbnail swap for main product image
  --------------------------------------------------------------------- */
  class ThemeProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = qs("[data-gallery-main]", this);
      qsa("[data-gallery-thumb]", this).forEach(
        function (thumb) {
          thumb.addEventListener("click", function () {
            this.setActive(thumb);
          }.bind(this));
        }.bind(this)
      );
    }
    setActive(thumb) {
      if (!this.main) return;
      qsa("[data-gallery-thumb]", this).forEach(function (t) {
        t.classList.remove("is-active");
      });
      thumb.classList.add("is-active");
      var fullSrc = thumb.getAttribute("data-full-src");
      var img = qs("img", this.main);
      if (img && fullSrc) {
        img.src = fullSrc;
        img.srcset = "";
      }
    }
  }
  customElements.define("theme-product-gallery", ThemeProductGallery);

  /* ---------------------------------------------------------------------
     Money formatting — mirrors Shopify's {{ amount }} money_format tokens
     without needing shop.js, using the money_format the page provides.
  --------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    format = format || (window.themeMoneyFormat || "${{amount}}");
    var value = (cents / 100).toFixed(2);
    var withComma = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var noDecimals = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return format
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals);
  }

  /* ---------------------------------------------------------------------
     <theme-variant-picker> — keeps hidden variant id, price and add-to-cart
     availability in sync with the selected options (product + featured
     product sections, and the product page).
  --------------------------------------------------------------------- */
  class ThemeVariantPicker extends HTMLElement {
    connectedCallback() {
      try {
        this.productData = JSON.parse(this.dataset.productJson);
      } catch (e) {
        return;
      }
      this.container = this.closest("[data-product-info]") || this;
      this.hiddenInput = qs("[data-variant-id]", this);
      qsa("select", this).forEach(
        function (select) {
          select.addEventListener("change", this.onChange.bind(this));
        }.bind(this)
      );
    }
    onChange() {
      var selected = qsa("select", this)
        .sort(function (a, b) {
          return a.dataset.optionPosition - b.dataset.optionPosition;
        })
        .map(function (s) {
          return s.value;
        });
      var variant = this.productData.variants.find(function (v) {
        var opts = [v.option1, v.option2, v.option3].filter(function (o) {
          return o !== null;
        });
        return selected.every(function (val, i) {
          return opts[i] === val;
        });
      });
      if (!variant) return;
      if (this.hiddenInput) this.hiddenInput.value = variant.id;
      document.dispatchEvent(new CustomEvent("theme:variant:change", { detail: { variant: variant, container: this.container } }));
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateMedia(variant);
    }
    updatePrice(variant) {
      var priceEl = qs("[data-price-wrapper]", this.container);
      if (!priceEl) return;
      var onSale = variant.compare_at_price > variant.price;
      priceEl.innerHTML =
        '<div class="price' +
        (onSale ? " price--on-sale" : "") +
        '">' +
        (onSale
          ? '<span class="price__sale">' + formatMoney(variant.price) + '</span><span class="price__regular">' + formatMoney(variant.compare_at_price) + "</span>"
          : '<span class="price__regular">' + formatMoney(variant.price) + "</span>") +
        "</div>";
    }
    updateAvailability(variant) {
      var btn = qs("[data-add-to-cart]", this.container);
      if (!btn) return;
      btn.disabled = !variant.available;
      btn.textContent = variant.available ? btn.getAttribute("data-add-label") : btn.getAttribute("data-sold-out-label");
    }
    updateMedia(variant) {
      if (!variant.featured_image) return;
      var imageUrl = variant.featured_image.src || variant.featured_image;
      if (!imageUrl) return;
      var normalizedImageUrl = imageUrl.split("?")[0];
      var gallery = qs("theme-product-gallery", this.container);
      if (gallery) {
        var matchingThumb = qsa("[data-gallery-thumb]", gallery).find(function (thumb) {
          var thumbUrl = thumb.getAttribute("data-full-src") || "";
          return thumbUrl.split("?")[0] === normalizedImageUrl;
        });
        if (matchingThumb && typeof gallery.setActive === "function") {
          gallery.setActive(matchingThumb);
          return;
        }
      }
      var productImage = qs(".hover-zoom img", this.container);
      if (productImage) {
        productImage.src = imageUrl;
        productImage.srcset = "";
      }
    }
  }
  customElements.define("theme-variant-picker", ThemeVariantPicker);

  /* ---------------------------------------------------------------------
     <theme-quantity-selector>
  --------------------------------------------------------------------- */
  class ThemeQuantitySelector extends HTMLElement {
    connectedCallback() {
      this.input = qs("input", this);
      qsa("button", this).forEach(
        function (btn) {
          btn.addEventListener("click", this.step.bind(this, btn.getAttribute("data-step")));
        }.bind(this)
      );
    }
    step(direction) {
      var value = parseInt(this.input.value, 10) || 1;
      var min = parseInt(this.input.min, 10) || 1;
      value = direction === "up" ? value + 1 : Math.max(min, value - 1);
      this.input.value = value;
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
  customElements.define("theme-quantity-selector", ThemeQuantitySelector);

  /* ---------------------------------------------------------------------
     Cart — AJAX add / update / remove + drawer + free shipping bar
  --------------------------------------------------------------------- */
  var CartAPI = {
    get: function () {
      return fetch(routes.cartGet, { cache: "no-store" }).then(function (r) {
        return r.json();
      });
    },
    add: function (id, quantity) {
      return fetch(routes.cartAdd, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: id, quantity: quantity || 1 }),
      }).then(function (r) {
        return r.json();
      });
    },
    changeLine: function (line, quantity) {
      return fetch(routes.cartChange, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: line, quantity: quantity }),
      }).then(function (r) {
        return r.json();
      });
    },
  };
  window.ThemeCartAPI = CartAPI;

  class ThemeCartDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      qsa("[data-cart-open]").forEach(
        function (btn) {
          btn.addEventListener("click", this.open.bind(this));
        }.bind(this)
      );
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && this.classList.contains("is-open")) this.close();
      }.bind(this));
      this.addEventListener("click", this.handleCartClick.bind(this));
      this.addEventListener("change", this.handleQuantityChange.bind(this));
      document.addEventListener("theme:cart:added", this.open.bind(this));
      document.addEventListener("theme:cart:updated", this.render.bind(this));
    }
    open() {
      this.classList.add("is-open");
      document.body.style.overflow = "hidden";
      this.releaseFocus = trapFocus(qs(".cart-drawer__panel", this));
    }
    close() {
      this.classList.remove("is-open");
      document.body.style.overflow = "";
      if (this.releaseFocus) this.releaseFocus();
    }
    handleCartClick(e) {
      if (e.target.closest("[data-cart-close]")) {
        e.preventDefault();
        this.close();
        return;
      }
      var removeBtn = e.target.closest("[data-cart-remove-line]");
      if (!removeBtn) return;
      e.preventDefault();
      var line = parseInt(removeBtn.getAttribute("data-cart-remove-line"), 10);
      this.setLoading(true);
      CartAPI.changeLine(line, 0).then(this.render.bind(this));
    }
    handleQuantityChange(e) {
      var input = e.target.closest("[data-cart-line-qty]");
      if (!input) return;
      var line = parseInt(input.getAttribute("data-cart-line-qty"), 10);
      this.setLoading(true);
      CartAPI.changeLine(line, parseInt(input.value, 10) || 0).then(this.render.bind(this));
    }
    setLoading(isLoading) {
      var body = qs(".cart-drawer__body", this);
      if (body) body.classList.toggle("is-updating", isLoading);
    }
    render(eventOrCart) {
      var cart = eventOrCart && eventOrCart.detail ? eventOrCart.detail : eventOrCart;
      var promise = cart && cart.items ? Promise.resolve(cart) : CartAPI.get();
      promise.then(
        function (cart) {
          document.dispatchEvent(new CustomEvent("theme:cart:count", { detail: cart }));
          this.setLoading(false);
          if (window.Shopify && window.Shopify.onCartUpdate) window.Shopify.onCartUpdate(cart);
          this.updateCountBubble(cart.item_count);
          this.refreshMarkup(cart);
        }.bind(this)
      );
    }
    refreshMarkup(cart) {
      var refreshUrl = new URL(window.location.href);
      refreshUrl.searchParams.set("_cart_refresh", Date.now());
      fetch(refreshUrl.toString(), { cache: "no-store", headers: { Accept: "text/html" } })
        .then(function (response) {
          if (!response.ok) throw new Error("Cart markup request failed");
          return response.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, "text/html");
            var nextPanel = qs("#CartDrawer .cart-drawer__panel", parsed);
            var currentPanel = qs(".cart-drawer__panel", this);
            if (nextPanel && currentPanel) currentPanel.innerHTML = nextPanel.innerHTML;
            this.updateFreeShippingBar(cart.total_price);
            this.setLoading(false);
          }.bind(this)
        )
        .catch(
          function () {
            this.setLoading(false);
            window.location.assign("/cart");
          }.bind(this)
        );
    }
    updateCountBubble(count) {
      qsa("[data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }
    updateFreeShippingBar(totalCents) {
      var bar = qs("[data-free-shipping-bar]", this);
      if (!bar) return;
      var threshold = parseFloat(bar.getAttribute("data-threshold")) * 100;
      var fill = qs(".free-shipping-bar__fill", bar);
      var text = qs(".free-shipping-bar__text", bar);
      var remaining = Math.max(0, threshold - totalCents);
      var pct = Math.min(100, (totalCents / threshold) * 100);
      if (fill) fill.style.width = pct + "%";
      if (text) {
        text.textContent =
          remaining === 0
            ? text.getAttribute("data-success-text")
            : text.getAttribute("data-progress-text").replace(
                "{{ amount }}",
                (remaining / 100).toLocaleString(undefined, { style: "currency", currency: "USD" })
              );
      }
    }
  }
  customElements.define("theme-cart-drawer", ThemeCartDrawer);

  /* ---------------------------------------------------------------------
     <theme-product-form> — add to cart (product page + quick add)
  --------------------------------------------------------------------- */
  class ThemeProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs("form", this);
      if (!this.form) return;
      this.submitBtn = qs('[type="submit"]', this.form);
      this.form.addEventListener("submit", this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      if (!this.submitBtn) return;
      this.submitBtn.classList.add("btn--loading");
      this.submitBtn.disabled = true;
      var formData = new FormData(this.form);
      var id = formData.get("id");
      var quantity = formData.get("quantity") || 1;
      CartAPI.add(id, quantity)
        .then(
          function (result) {
            if (result.status) {
              this.showError(result.description || "This item could not be added to your cart.");
              return;
            }
            document.dispatchEvent(new CustomEvent("theme:cart:added", { detail: result }));
            document.dispatchEvent(new CustomEvent("theme:cart:updated"));
          }.bind(this)
        )
        .catch(function () {
          this.showError("Something went wrong. Please try again.");
        }.bind(this))
        .finally(
          function () {
            this.submitBtn.classList.remove("btn--loading");
            this.submitBtn.disabled = false;
          }.bind(this)
        );
    }
    showError(message) {
      var errorEl = qs("[data-form-error]", this);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        window.alert(message);
      }
    }
  }
  customElements.define("theme-product-form", ThemeProductForm);

  /* ---------------------------------------------------------------------
     <theme-newsletter-form> — AJAX-friendly progressive enhancement
  --------------------------------------------------------------------- */
  class ThemeNewsletterForm extends HTMLElement {
    connectedCallback() {
      this.form = qs("form", this);
      if (!this.form) return;
      this.form.addEventListener("submit", this.onSubmit.bind(this));
    }
    onSubmit(e) {
      var email = qs('input[type="email"]', this.form);
      if (email && !email.checkValidity()) {
        e.preventDefault();
        this.showMessage(email.validationMessage || "Please enter a valid email address.", "error");
      }
    }
    showMessage(text, type) {
      var el = qs("[data-form-message]", this);
      if (!el) return;
      el.textContent = text;
      el.className = "form-message form-message--" + type;
      el.hidden = false;
    }
  }
  customElements.define("theme-newsletter-form", ThemeNewsletterForm);

  /* ---------------------------------------------------------------------
     Init
  --------------------------------------------------------------------- */
  document.addEventListener("DOMContentLoaded", function () {
    initRevealAnimations();
    initLazyImages();
    initStickyHeader();
    initMegaMenu();
  });
})();
